import { $$, $ } from "@utils/dom";
import { DATA_ATTR } from "@constants/data-attributes";

// === STATE ===
// Global lock - prevents ALL audio operations while one is in progress
let isProcessing = false;
// Current audio element
let currentAudio: HTMLAudioElement | null = null;
// Current button being used
let currentButton: HTMLButtonElement | null = null;
// Current waveform progress element
let currentWaveformProgress: HTMLImageElement | null = null;
// Animation frame ID
let animationFrameId: number | null = null;
// Safety timeout ID
let safetyTimeoutId: ReturnType<typeof setTimeout> | null = null;

// === CONSTANTS ===
const SAFETY_TIMEOUT_MS = 30000; // 30 seconds max for any operation

// === HELPERS ===

/**
 * Extract element ID from audio path
 */
function extractElementId(audioPath: string): string | null {
  const match = audioPath.match(/\/([^/]+)\.wav$/);
  return match?.[1] ?? null;
}

/**
 * Set button to loading state (spinner icon)
 */
function setButtonLoading(button: HTMLButtonElement): void {
  const use = button.querySelector("use");
  if (use) {
    use.setAttribute("href", "#iconSpinner");
    use.closest("svg")?.classList.add("spinner");
  }
}

/**
 * Set button to ready state (play icon)
 */
function setButtonReady(button: HTMLButtonElement | null): void {
  if (!button) return;
  const use = button.querySelector("use");
  if (use) {
    use.setAttribute("href", "#iconPlay");
    use.closest("svg")?.classList.remove("spinner");
  }
}

/**
 * Reset waveform progress to initial state
 */
function resetWaveformProgress(waveformImg: HTMLImageElement | null): void {
  if (waveformImg) {
    waveformImg.style.clipPath = "inset(0 100% 0 0)";
  }
}

/**
 * Stop the animation loop
 */
function stopAnimation(): void {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
}

/**
 * Animate waveform progress at 60fps
 */
function animateProgress(): void {
  if (!currentAudio || !currentWaveformProgress) return;

  if (!currentAudio.paused && !currentAudio.ended) {
    const progress = currentAudio.currentTime / currentAudio.duration;
    if (!isNaN(progress)) {
      const clipRight = 100 - progress * 100;
      currentWaveformProgress.style.clipPath = `inset(0 ${clipRight}% 0 0)`;
    }
    animationFrameId = requestAnimationFrame(animateProgress);
  }
}

/**
 * Full cleanup of all audio state
 */
function cleanup(): void {
  // Clear safety timeout
  if (safetyTimeoutId !== null) {
    clearTimeout(safetyTimeoutId);
    safetyTimeoutId = null;
  }

  // Stop animation
  stopAnimation();

  // Reset waveform
  resetWaveformProgress(currentWaveformProgress);

  // Reset button to play icon
  setButtonReady(currentButton);

  // Clean up audio element
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.src = "";
    currentAudio.load(); // Helps release resources
    currentAudio = null;
  }

  // Clear references
  currentWaveformProgress = null;
  currentButton = null;
}

/**
 * Handle audio ended event
 */
function handleAudioEnded(): void {
  cleanup();
  isProcessing = false;
}

/**
 * Handle play button click
 * Production-safe: Only ONE operation at a time
 */
async function handlePlayClick(event: MouseEvent): Promise<void> {
  const button = event.currentTarget as HTMLButtonElement;
  const audioSrc = button.dataset.playButton;

  if (!audioSrc) return;

  // CRITICAL: Block ALL clicks while processing
  if (isProcessing) {
    // Exception: If clicking same button that's playing, stop it
    if (button === currentButton && currentAudio && !currentAudio.paused) {
      cleanup();
      isProcessing = false;
    }
    return;
  }

  // Lock immediately
  isProcessing = true;

  // Safety timeout - auto-recover after 30 seconds
  safetyTimeoutId = setTimeout(() => {
    console.warn("Audio operation timed out, resetting...");
    cleanup();
    isProcessing = false;
  }, SAFETY_TIMEOUT_MS);

  try {
    // Clean up any previous state
    cleanup();

    // Set up new button
    currentButton = button;
    setButtonLoading(button);

    // Find waveform element
    const elementId = extractElementId(audioSrc);
    if (elementId) {
      const waveformPicture = $<HTMLPictureElement>(
        `[data-waveform-progress="${elementId}"]`,
      );
      if (waveformPicture) {
        currentWaveformProgress = $<HTMLImageElement>("img", waveformPicture);
      }
    }

    // Create fresh audio element
    currentAudio = new Audio(audioSrc);
    currentAudio.addEventListener("ended", handleAudioEnded, { once: true });

    // Wait for audio to start playing
    await currentAudio.play();

    // Clear safety timeout since we succeeded
    if (safetyTimeoutId !== null) {
      clearTimeout(safetyTimeoutId);
      safetyTimeoutId = null;
    }

    // Success - update UI
    setButtonReady(currentButton);
    animateProgress();

    // Keep isProcessing = true while audio is playing
    // It will be set to false in handleAudioEnded

  } catch (error) {
    // Clear safety timeout
    if (safetyTimeoutId !== null) {
      clearTimeout(safetyTimeoutId);
      safetyTimeoutId = null;
    }

    // Handle specific errors silently
    if (error instanceof DOMException) {
      if (error.name === "AbortError" || error.name === "NotAllowedError") {
        cleanup();
        isProcessing = false;
        return;
      }
    }

    // Log unexpected errors
    console.error("Audio playback failed:", error);
    cleanup();
    isProcessing = false;
  }
}

/**
 * Initialize audio player
 */
export function initAudioPlayer(): void {
  const playButtons = $$<HTMLButtonElement>(DATA_ATTR.PLAY_BUTTON);

  playButtons.forEach((button) => {
    button.addEventListener("click", handlePlayClick);
  });
}
